<template>
    <div>
        <div class="professionals__page-header">
            <div class="container">
                <h2 class="professionals__page-title">Our Team</h2>
                <div class="professionals__page-search">
                    <form @submit.prevent="searchContent">
                        <div class="professionals__input-wrapper professionals__input-wrapper--small">
                            <input type="text" placeholder="Enter name or keyword" name="query" v-model="filters.query" />
                            <button title="Search" @click="searchContent" type="button">
                                <i class="icon-search"></i>
                            </button>
                        </div>
                        <label class="professionals-checkbox" v-if="this.engine == 'elastic'">
                            <span>Search by Name Only</span>
                            <input type="checkbox" v-model="name_only" />
                            <span class="professionals-checkbox__check"></span>
                        </label>
                    </form>
                </div>
            </div>
        </div>
        <div class="page-header">
            <div class="professionals__page-filters professionals__page-filters--full">
                <div class="professionals__filters-wrapper">
                    <div class="professionals__filters-toggle professionals__filters-toggle--mobile" @click="toggleFilters">
                        <div class="professionals__filters-title">Filter</div>
                        <div class="professionals__filters-icon">
                            <i class="icon-chevron-down"></i>
                        </div>
                    </div>
                </div>
                <Transition>
                    <div class="container professionals__page-filters-container" :class="{
                        'professionals__page-filters-container--active': showFilters,
                    }" v-if="filterOptions">
                        <Dropdown @update="updateFilters" :value="filters.location" attribute="location"
                            :options="filterOptions.locations" title="Office" />
                        <Dropdown @update="updateFilters" :value="filters.practice" attribute="practice"
                            :options="filterOptions.practices" title="Practice" />
                        <Dropdown @update="updateFilters" :value="filters.industry" attribute="industry"
                            :options="filterOptions.industries" title="Industry" />
                        <Dropdown @update="updateFilters" :value="filters.education" attribute="education"
                            :options="filterOptions.education" title="Education" />
                        <Dropdown @update="updateFilters" :value="filters.language" attribute="language"
                            :options="filterOptions.languages" title="Language" />
                        <Dropdown @update="updateFilters" :value="filters.title" attribute="title"
                            :options="filterOptions.title" title="Title" />
                        <Dropdown @update="updateFilters" :value="filters.lastName" attribute="lastName"
                            :options="filterOptions.lastName" title="Last Name" />
                        <Dropdown @update="updateFilters" :value="filters.region" attribute="region"
                            :options="filterOptions.regions" title="Region" />
                    </div>
                </Transition>
            </div>
        </div>
        <Transition>
            <div class="container professionals__container">
                <div class="professionals__secondary-filters">
                    <div class="professionals__sort-dropdown" v-if="this.engine == 'elastic'">
                        <div class="news-block__cards-filter" v-show="isSearchQuery">
                            <select name="filterBy" @change="searchContent" v-model="search_filter">
                                <option value="relevance">Sort by Relevance</option>
                                <option value="title">Sort by Title</option>
                            </select>
                            <i class="icon-chevron-down"></i>
                        </div>
                    </div>
                    <div class="professionals__clear-filters">
                        <button v-show="filtersActive" @click="clearFilters">
                            Clear Search Filters
                        </button>
                    </div>
                </div>
                <div v-if="!loading">
                    <div class="professionals__wrapper" v-if="content.professionals && content.professionals.length > 0">
                        <ProfessionalCard :professional="professional"
                            v-for="(professional, profIndex) in content.professionals" :key="profIndex"></ProfessionalCard>
                    </div>
                    <div v-else>
                        <div v-if="hasSearched" class="professionals__empty">
                            Your search did not return any results.
                        </div>
                    </div>
                </div>
                <div v-if="!loading && !hasSearched">
                    <div class="professionals__empty">
                        Search for a member of our team
                    </div>
                </div>
                <Pagination v-if="!loading && pagination && pagination.totalPages > 1"
                    @pagination-click="handlePaginationClick" :pagination="pagination" />
                <LoadingIndicator v-if="loading" />
            </div>
        </Transition>
    </div>
</template>

<script>
import axios from "axios";

// Components
import Pagination from "../Global/Pagination.vue";
import LoadingIndicator from "../Global/LoadingIndicator.vue";
import ErrorIndicator from "../Global/ErrorIndicator.vue";
import Dropdown from "../FormFields/Dropdown.vue";
import ProfessionalCard from "../Professionals/ProfessionalCard.vue";

export default {
    props: ["engine"],
    components: {
        LoadingIndicator,
        ErrorIndicator,
        Dropdown,
        ProfessionalCard,
        Pagination,
    },
    data() {
        return {
            error: false,
            loading: true,
            content: [],
            pagination: {
                perPage: 12,
                totalPages: 1,
                currentPage: 1,
            },
            filterOptions: null,
            locations: [],
            services: [],
            filters: {},
            search_filter: "title",
            showFilters: false,
            hasSearched: false,
            hasClickedSearched: false,
            isSearchQuery: false,
            name_only: false,
        };
    },
    computed: {
        filtersActive() {
            let active = false;
            Object.keys(this.filters).forEach((key) => {
                if (this.filters[key] != "") {
                    active = true;
                }
            });
            return active;
        },
    },
    mounted() {
        window.scrollTo({
            top: 0,
        });
        let urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has("query")) {
            this.filters.query = urlParams.get("query");
        }
        if (urlParams.has("location")) {
            this.filters.location = urlParams.get("location");
        }
        if (urlParams.has("practice")) {
            this.filters.practice = urlParams.get("practice");
        }
        if (urlParams.has("industry")) {
            this.filters.industry = urlParams.get("industry");
        }
        if (urlParams.has("education")) {
            this.filters.education = urlParams.get("education");
        }
        if (urlParams.has("lastName")) {
            this.filters.lastName = urlParams.get("lastName");
        }
        if (urlParams.has("language")) {
            this.filters.language = urlParams.get("language");
        }
        if (urlParams.has("title")) {
            this.filters.title = urlParams.get("title");
        }
        if (urlParams.has("region")) {
            this.filters.region = urlParams.get("region");
        }
        if (urlParams.has("filter")) {
            this.search_filter = urlParams.get("filter");
        }
        if (urlParams.has("name_only")) {
            this.name_only = true;
        }

        this.scrollToTop();
        this.initializeFromUrl();
        this.getFilters();

        // Add the popstate event listener
        window.addEventListener("popstate", this.handlePopstate);
    },
    beforeDestroy() {
        // Remove the popstate event listener
        window.removeEventListener("popstate", this.handlePopstate);
    },
    methods: {
        scrollToTop() {
            window.scrollTo({ top: 0 });
        },
        initializeFromUrl() {
            const urlParams = new URLSearchParams(window.location.search);
            this.filters = {
                query: this.getUrlParam(urlParams, "query"),
                location: this.getUrlParam(urlParams, "location"),
                practice: this.getUrlParam(urlParams, "practice"),
                industry: this.getUrlParam(urlParams, "industry"),
                education: this.getUrlParam(urlParams, "education"),
                region: this.getUrlParam(urlParams, "region"),
                lastName: this.getUrlParam(urlParams, "lastName"),
                language: this.getUrlParam(urlParams, "language"),
                title: this.getUrlParam(urlParams, "title"),
            };
            this.pagination.currentPage =
                parseInt(this.getUrlParam(urlParams, "page")) || 1;
            this.search_filter =
                this.getUrlParam(urlParams, "filter") || "title";

            // Update content based on the new filters and pagination
            this.getContent(true);
        },
        getUrlParam(urlParams, paramName) {
            return urlParams.get(paramName) || "";
        },
        clearFilters: function () {
            this.filters = {};
            this.name_only = false;
            this.search_filter = "title";
            this.isSearchQuery = true;
            this.getContent();
        },
        toggleFilters: function () {
            this.showFilters = !this.showFilters;
        },
        handlePaginationClick: function (payload) {
            this.pagination.currentPage = payload;
            this.getContent();
        },
        updateFilters: function (payload) {
            this.pagination.currentPage = 1;
            this.filters[payload.attribute] = payload.value;
            this.getContent();
            this.toggleFilters();
        },
        getFilters: function () {
            this.loading = true;
            axios
                .post(
                    "/api/professionals/filters?culture=en",
                    {},
                    {
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    }
                )
                .then((response) => {
                    this.filterOptions = response.data;
                })
                .catch((err) => {
                    this.error = true;
                });
        },
        searchContent: function () {
            this.pagination.currentPage = 1;
            this.filters;
            this.getContent();
        },
        getContent: function (isPopState = false) {
            this.loading = true;

            let filters = [];

            Object.keys(this.filters).forEach((key) => {
                if (this.filters[key] != "") {
                    filters.push(`&${key}=${encodeURIComponent(this.filters[key])}`);
                }
            });

            if (this.name_only) {
                filters.push(`&name_only=true`);
            }

            filters.push(`&engine=${encodeURIComponent(this.engine)}`);

            const urlParams = new URLSearchParams(window.location.search);
            if (this.isSearchQuery === false && this.filters?.query && (this.filters?.query?.length || 0) > 0 && !urlParams.has("query") && this.search_filter === "title") {
                this.search_filter = "relevance";
            }

            if (!this.filters.query) {
                this.search_filter = "title";
            }

            filters.push(`&filter=${encodeURIComponent(this.search_filter)}`);

            this.hasSearched = true;

            axios
                .post(
                    `/api/professionals?culture=en&page=${this.pagination.currentPage
                    }${filters.join("")}`,
                    {},
                    {
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    }
                )
                .then((response) => {

                    this.content = response.data;
                    this.pagination = response.data.pagination;

                    const url = `/professionals?page=${this.pagination.currentPage}${filters.join("")}`;
                    if (!isPopState && (!history.state || history.state.page != url)) {
                        history.pushState({ currentPage: this.currentPage }, "Professionals | Page " + this.currentPage, url);
                    }

                    this.isSearchQuery = this.content.isSearchQuery || false;
                    this.loading = false;
                })
                .catch((err) => {
                    this.loading = false;
                    this.error = true;
                });
        },
        handlePopstate(event) {
            this.initializeFromUrl();
        },
    },
};
</script>

<style scoped>
/* we will explain what these classes do next */
.v-enter-active,
.v-leave-active {
    transition: opacity 300ms ease-in-out;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
